import React, { useContext, useLayoutEffect, useMemo, useRef } from 'react';
import { DatabaseContext } from '../../redux/DatabaseStore';
import AuthContext from '../../components/AuthContext';
import { LocaleContext } from '../../components/LocaleContext';
import { createMenuTabAction, isItemVisible, itemAllowedForChildren } from './menuSearch';

/** Posunie scroll kontajnera tak, aby bola celá rozbalená položka viditeľná (reaguje na aktuálnu výšku okna). */
function scrollMenuItemIntoView(scrollContainer, element) {
  if (!scrollContainer || !element) return;

  const padding = 12;
  const containerRect = scrollContainer.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();
  const containerHeight = scrollContainer.clientHeight;

  const relativeTop = elementRect.top - containerRect.top + scrollContainer.scrollTop;
  const relativeBottom = relativeTop + elementRect.height;

  const visibleTop = scrollContainer.scrollTop + padding;
  const visibleBottom = scrollContainer.scrollTop + containerHeight - padding;

  if (relativeBottom > visibleBottom) {
    scrollContainer.scrollTop = relativeBottom - containerHeight + padding;
  }
  if (relativeTop < scrollContainer.scrollTop + padding) {
    scrollContainer.scrollTop = relativeTop - padding;
  }
}

/** Shared row classes: icon never shrinks; label wraps up to 2 lines. */
const MENU_ROW =
  'flex items-start w-full min-w-0 p-2 gap-3 rounded-md group text-white hover:bg-[#555]';
const MENU_ICON_SLOT = 'menu-item-icon shrink-0 grow-0 w-6 h-6 flex items-center justify-center mt-0.5';
const MENU_LABEL =
  'menu-item-label text-gray-100 flex-1 min-w-0 text-left break-words [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2] overflow-hidden';
const MENU_CHEVRON = 'menu-item-chevron shrink-0 grow-0 mt-0.5';

function depthPad(depth) {
  if (depth === 2) return 'pl-6 ';
  if (depth === 3) return 'pl-9 ';
  if (depth === 4) return 'pl-12 ';
  return ' ';
}

function Menu({ items, depth, searchMode, displayChildren, setDisplayChildren, menuOpen, setMenuOpen, menuScrollRef, parentAllowed = true, parentTitle }) {
  const [state, dispatch] = useContext(DatabaseContext);
  const { user } = useContext(AuthContext);
  const { t } = useContext(LocaleContext);
  const prevDisplayChildrenRef = useRef(displayChildren);

  const visibleItems = useMemo(
    () => (items || []).filter((item) => isItemVisible(item, user?.permissions, parentAllowed)),
    [items, user?.permissions, parentAllowed]
  );

  useLayoutEffect(() => {
    if (depth !== 1 || menuOpen || !menuScrollRef?.current) return;

    const scrollContainer = menuScrollRef.current;
    const prev = prevDisplayChildrenRef.current;

    Object.entries(displayChildren).forEach(([id, isExpanded]) => {
      if (isExpanded && !prev[id]) {
        const el = scrollContainer.querySelector(`[data-menu-item-id="${id}"]`);
        if (el) scrollMenuItemIntoView(scrollContainer, el);
      }
    });

    prevDisplayChildrenRef.current = displayChildren;
  }, [displayChildren, depth, menuOpen, menuScrollRef]);

  return (
    <ul
      className={`${
        depth === 3 || depth == 4 ? 'bg-[#333] ' : ' '
      } rounded-sm pt-2 pb-2 space-y-1 text-sm`}
    >
      {visibleItems.map((item) => (
        <React.Fragment key={item.id}>
          {item.children ? (
            <li className="rounded-sm " data-menu-item-id={item.id}>
              {item.children && (
                <button
                  type="button"
                  className={`${depthPad(depth)}${MENU_ROW}`}
                  onClick={() => {
                    setDisplayChildren({
                      ...displayChildren,
                      [item.id]: !displayChildren[item.id],
                    });
                    if (menuOpen) setMenuOpen(false);
                  }}
                >
                  {depth === 1 && item.icon ? (
                    <span className={MENU_ICON_SLOT} data-testid="menu-item-icon" aria-hidden="true">
                      {item.icon}
                    </span>
                  ) : null}
                  {!menuOpen && (
                    <>
                      <span className={MENU_LABEL} data-testid="menu-item-label">
                        {t(item.title || item.page)}
                      </span>
                      <i
                        className={`${MENU_CHEVRON} pi ${!displayChildren[item.id] ? 'pi-angle-down' : 'pi-angle-up'}`}
                        aria-hidden="true"
                      />
                    </>
                  )}
                </button>
              )}
              {displayChildren[item.id] && item.children && (
                <Menu
                  items={item.children}
                  depth={depth + 1}
                  searchMode={searchMode}
                  displayChildren={displayChildren}
                  setDisplayChildren={setDisplayChildren}
                  menuOpen={menuOpen}
                  setMenuOpen={setMenuOpen}
                  menuScrollRef={menuScrollRef}
                  parentAllowed={itemAllowedForChildren(item, user?.permissions, parentAllowed)}
                  parentTitle={item.title || item.page || parentTitle}
                />
              )}
            </li>
          ) : (
            <li className={`${depth === 3 ? 'bg-[#333] ' : ' '}`}>
              <button
                onClick={() => {
                  const action = createMenuTabAction(item, parentTitle, t);
                  if (action) dispatch(action);
                }}
                className={`${depthPad(depth)}${MENU_ROW} transition duration-75 rounded-lg`}
              >
                {!menuOpen && (
                  <span className={MENU_LABEL} data-testid="menu-item-label">
                    {t(item.title || item.page)}
                  </span>
                )}
              </button>
            </li>
          )}
        </React.Fragment>
      ))}
    </ul>
  );
}

export default Menu;
